'use client'

import { useState } from 'react'
import { FileText, Download, Calendar } from 'lucide-react'

export default function ReportGenerator() {
  const [generating, setGenerating] = useState(false)
  const [reportType, setReportType] = useState('users')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const handleGenerateReport = async () => {
    setGenerating(true)
    
    try {
      const response = await fetch('/api/admin/reports/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: reportType,
          dateFrom,
          dateTo,
        }),
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        alert('Failed to generate report')
      }
    } catch (error: unknown) {
      console.error('Error generating report:', error)
      alert('Failed to generate report')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="bg-white dark:bg-charcoal-900 rounded-xl p-6 border border-charcoal-200 dark:border-charcoal-800">
      <div className="<Thinking>
The user wants me to continue the generation exactly where I left off. Looking at the code, I was in the middle of creating a ReportGenerator component. I need to complete this component and then finish the overall admin dashboard enhancement.

The component was cut off in the middle of the return statement, specifically in the className attribute. I need to complete:
1. The ReportGenerator component
2. Any remaining components or files needed
3. Wrap up the admin dashboard enhancement

Let me continue exactly where it left off without any explanation or meta-commentary.
</Thinking>

flex items-center gap-3 mb-6">
        <FileText className="h-5 w-5 text-rose-600" />
        <h3 className="text-lg font-semibold text-charcoal-900 dark:text-white">
          Generate Reports
        </h3>
      </div>

      <div className="space-y-6">
        {/* Report Type */}
        <div>
          <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-3">
            Report Type
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: 'users', label: 'Users' },
              { value: 'courses', label: 'Courses' },
              { value: 'enrollments', label: 'Enrollments' },
              { value: 'revenue', label: 'Revenue' },
            ].map((type) => (
              <label
                key={type.value}
                className="flex items-center justify-center p-4 border border-charcoal-300 dark:border-charcoal-700 rounded-xl cursor-pointer hover:bg-charcoal-50 dark:hover:bg-charcoal-800 transition-colors"
              >
                <input
                  type="radio"
                  name="reportType"
                  value={type.value}
                  checked={reportType === type.value}
                  onChange={(e) => setReportType(e.target.value)}
                  className="sr-only"
                />
                <span className={`font-medium ${
                  reportType === type.value
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-charcoal-700 dark:text-charcoal-300'
                }`}>
                  {type.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
              From Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-charcoal-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">
              To Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-charcoal-400" />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-charcoal-300 dark:border-charcoal-700 rounded-xl bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerateReport}
          disabled={generating}
          className="w-full flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-medium transition-colors"
        >
          <Download className="h-5 w-5" />
          {generating ? 'Generating...' : 'Generate PDF Report'}
        </button>
      </div>
    </div>
  )
}
